import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';
import { ConsumerLayout } from '@/components/consumer/ConsumerLayout';
import { useCriarSolicitacao } from '@/hooks/useSolicitacoes';
import { useCategorias } from '@/hooks/useMarketplace';

export default function ConsumerPedidoNovo() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: categorias } = useCategorias();
  const criar = useCriarSolicitacao();

  const [categoriaId, setCategoriaId] = useState('');
  const [cidade, setCidade] = useState('');
  const [descricao, setDescricao] = useState('');
  const [fotos, setFotos] = useState<File[]>([]);

  const podeSubmeter = categoriaId && cidade.trim() && descricao.trim() && !criar.isPending;

  const onPickFotos = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []).slice(0, 3);
    setFotos(files);
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const id = await criar.mutateAsync({ categoriaId, cidade: cidade.trim(), descricao: descricao.trim(), fotos });
      navigate(`/app/pedidos/${id}`, { replace: true });
    } catch {
      // erro já tratado (toast) no hook; fica na página para retry
    }
  };

  return (
    <ConsumerLayout>
      <div className="flex items-center gap-2 mb-4">
        <Link to="/app/pedidos" aria-label={t('myOrders.back')}><ArrowLeft className="h-5 w-5" /></Link>
      </div>
      <h1 className="text-lg font-bold text-foreground mb-4">{t('requests.newTitle')}</h1>
      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <label htmlFor="pedido-categoria" className="block text-sm font-medium mb-1">{t('requests.category')}</label>
          <select
            id="pedido-categoria"
            value={categoriaId}
            onChange={(e) => setCategoriaId(e.target.value)}
            className="w-full rounded-md border border-border bg-background px-3 py-2"
            required
          >
            <option value="" disabled>—</option>
            {(categorias ?? []).map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="pedido-cidade" className="block text-sm font-medium mb-1">{t('requests.city')}</label>
          <input
            id="pedido-cidade"
            value={cidade}
            onChange={(e) => setCidade(e.target.value)}
            className="w-full rounded-md border border-border bg-background px-3 py-2"
            required
          />
        </div>
        <div>
          <label htmlFor="pedido-descricao" className="block text-sm font-medium mb-1">{t('requests.description')}</label>
          <textarea
            id="pedido-descricao"
            value={descricao}
            onChange={(e) => setDescricao(e.target.value)}
            placeholder={t('requests.descriptionPlaceholder')}
            rows={4}
            className="w-full rounded-md border border-border bg-background px-3 py-2"
            required
          />
        </div>
        <div>
          <label htmlFor="pedido-fotos" className="block text-sm font-medium mb-1">{t('requests.photos')}</label>
          <input id="pedido-fotos" type="file" accept="image/*" multiple onChange={onPickFotos} className="w-full text-sm" />
          {fotos.length > 0 && <p className="text-xs text-muted-foreground mt-1">{fotos.length}/3</p>}
        </div>
        <button type="submit" disabled={!podeSubmeter} className="ow-btn ow-btn--primary w-full disabled:opacity-50">
          {t('requests.submit')}
        </button>
      </form>
    </ConsumerLayout>
  );
}
